import { useState } from 'react';
import { X, CheckCircle2, AlertTriangle, MessageSquare, Video, Mic, Zap, Cpu } from 'lucide-react';
import { submitInterviewIssueReport } from '../../../utils/api';

const issueTypes = [
  { id: 'voice_problem', label: 'Voice Problem', icon: Mic },
  { id: 'hallucination', label: 'AI Hallucination', icon: Zap },
  { id: 'ai_freeze', label: 'AI Freeze / No Response', icon: Cpu },
  { id: 'wrong_question', label: 'Wrong / Irrelevant Question', icon: MessageSquare },
  { id: 'technical_glitch', label: 'Technical Glitch', icon: AlertTriangle },
  { id: 'other', label: 'Other Issue', icon: MessageSquare },
];

const IssueReportModal = ({ sessionId, onClose }) => {
  const [selectedType, setSelectedType] = useState(null);
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    if (!selectedType) {
      setError('Please select an issue type.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await submitInterviewIssueReport(sessionId, selectedType, description);
      setIsSuccess(true);
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err) {
      setError(err.error || 'Failed to submit report. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-950/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[90vh] border border-slate-200 dark:border-slate-800">
        
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-white tracking-tight flex items-center gap-2.5">
            <div className="p-1.5 bg-rose-500/10 text-rose-500 rounded-lg">
              <AlertTriangle size={16} />
            </div>
            Report an Issue
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar flex-1 space-y-5">
          {isSuccess ? (
            <div className="flex flex-col items-center justify-center py-8 text-center space-y-3">
              <div className="w-12 h-12 bg-emerald-500/10 rounded-full flex items-center justify-center">
                <CheckCircle2 size={28} className="text-emerald-500" />
              </div>
              <h3 className="text-base font-semibold text-slate-800 dark:text-white tracking-tight">Report Submitted</h3>
              <p className="text-xs font-normal text-slate-500 dark:text-slate-400 max-w-xs leading-relaxed">
                Thank you for letting us know. Our team will review this issue shortly.
              </p>
            </div>
          ) : (
            <>
              <div>
                <label className="block text-[11px] font-medium tracking-wider text-slate-500 dark:text-slate-400 mb-2.5">
                  What type of issue are you experiencing?
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {issueTypes.map((type) => {
                    const Icon = type.icon;
                    const isSelected = selectedType === type.id;
                    return (
                      <button
                        key={type.id}
                        type="button"
                        onClick={() => {
                          setSelectedType(type.id);
                          setError(null);
                        }}
                        className={`flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border text-left transition-all ${
                          isSelected
                            ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30 text-emerald-900 dark:text-emerald-300 ring-1 ring-emerald-500 shadow-sm'
                            : 'border-slate-200 dark:border-slate-800 hover:border-emerald-500/40 text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800/40'
                        }`}
                      >
                        <Icon size={15} className={isSelected ? 'text-emerald-500' : 'text-slate-400'} />
                        <span className="text-xs font-normal tracking-wide">{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-medium  tracking-wider text-slate-500 dark:text-slate-400 mb-2">
                  Additional Details (Optional)
                </label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Tell us a bit more about what went wrong..."
                  className="w-full bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700/80 rounded-xl px-3.5 py-2 text-xs font-normal text-slate-800 dark:text-slate-100 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/40 min-h-[85px] resize-y"
                  maxLength={500}
                />
                <div className="text-right text-[10px] font-normal text-slate-400 mt-1">
                  {description.length}/500
                </div>
              </div>

              {error && (
                <div className="p-2.5 bg-rose-50 dark:bg-rose-950/30 text-rose-600 dark:text-rose-400 text-xs font-normal rounded-xl border border-rose-200 dark:border-rose-900/50 flex items-start gap-2">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {!isSuccess && (
          <div className="px-6 py-3 border-t border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50 flex justify-end gap-2">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="px-4 py-2 text-xs font-medium  tracking-wider text-slate-600 dark:text-slate-400 hover:bg-slate-200/60 dark:hover:bg-slate-800 rounded-xl transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting || !selectedType}
              className="px-4 py-2 text-xs font-semibold  tracking-wider text-white bg-emerald-600 hover:bg-emerald-700 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 shadow-sm"
            >
              {isSubmitting ? (
                <>
                  <svg className="animate-spin h-3.5 w-3.5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Submitting...
                </>
              ) : (
                'Submit Report'
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default IssueReportModal;
